import { api } from './api';
import { emptyProgress, loadProgress, saveProgress, safeGet, safeSet, safeDel } from './store';
import { isMultiBox, unitsFromBoxes } from './boxes';

/* Receipts follow the login from one device to another.

   Scanning stays on the device and never waits for the network. In the
   background the device sends what changed since the server last answered
   (+2 on a line, a new scan…), and takes the merged result back. Changes add
   up on the server, so two scanners on the same login can count the same
   truck together. Per order the device keeps the last server state (the
   base) next to its own. */

const baseKey = (poId) => `inny.progressBase.${poId}`;
const CLEARS_KEY = 'inny.progressClears'; // orders to clear on the server once it answers again
const pendingKey = (poId) => `inny.progressPending.${poId}`; // changes sent, answer not seen yet

const loadBase = (poId) => ({ ...emptyProgress(), ...(safeGet(baseKey(poId)) || {}) });
const saveBase = (poId, state) => safeSet(baseKey(poId), state);

const num = (v) => Number(v) || 0;
let clears = 0; // bumped by clearReceipt: an answer asked for before it is stale
const scanKey = (s) => `${s.at}|${s.barcode}|${s.line_id ?? ''}|${s.box_id ?? ''}`;

/** What `local` has that `base` hasn't: per-line and per-box differences, new scans. */
export function diff(local, base) {
  const counts = {};
  const lines = new Set([...Object.keys(local.counts || {}), ...Object.keys(base.counts || {})]);
  lines.forEach((l) => {
    const d = num(local.counts?.[l]) - num(base.counts?.[l]);
    if (d) counts[l] = d;
  });
  const boxes = {};
  const boxLines = new Set([...Object.keys(local.boxes || {}), ...Object.keys(base.boxes || {})]);
  boxLines.forEach((l) => {
    const a = local.boxes?.[l] || {};
    const b = base.boxes?.[l] || {};
    new Set([...Object.keys(a), ...Object.keys(b)]).forEach((box) => {
      const d = num(a[box]) - num(b[box]);
      if (d) (boxes[l] = boxes[l] || {})[box] = d;
    });
  });
  const known = new Set((base.scans || []).map(scanKey));
  const scans = (local.scans || []).filter((s) => !known.has(scanKey(s)));
  const startedAt = local.startedAt && local.startedAt !== base.startedAt ? local.startedAt : null;
  return { counts, boxes, scans, startedAt };
}

export const isEmpty = (c) => !Object.keys(c.counts).length && !Object.keys(c.boxes).length && !c.scans.length && !c.startedAt;

/** `state` plus a change, as the server does it (nothing below zero, a scan once). */
export function apply(state, change) {
  const counts = { ...(state.counts || {}) };
  Object.entries(change.counts || {}).forEach(([l, d]) => {
    counts[l] = Math.max(0, num(counts[l]) + num(d));
  });
  const boxes = Object.fromEntries(Object.entries(state.boxes || {}).map(([l, v]) => [l, { ...v }]));
  Object.entries(change.boxes || {}).forEach(([l, per]) => {
    boxes[l] = boxes[l] || {};
    Object.entries(per || {}).forEach(([box, d]) => {
      boxes[l][box] = Math.max(0, num(boxes[l][box]) + num(d));
    });
  });
  const seen = new Set((state.scans || []).map(scanKey));
  const scans = [...(state.scans || []), ...(change.scans || []).filter((s) => !seen.has(scanKey(s)))]
    .sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')))
    .slice(-1000);
  const started = [state.startedAt, change.startedAt].filter(Boolean).sort();
  return { counts, boxes, scans, startedAt: started[0] || null };
}

/** A multi-box line counts complete sets: after a merge, the boxes decide. */
export function settle(progress, lines) {
  const counts = { ...progress.counts };
  lines.forEach((line) => {
    if (isMultiBox(line) && progress.boxes?.[line.id]) counts[line.id] = unitsFromBoxes(line, progress.boxes[line.id]);
  });
  return { ...progress, counts };
}

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Split a combined progress (or change) back into its purchase orders. */
function splitByOrder(combined, progress) {
  const poOfLine = {};
  combined.lines.forEach((l) => {
    poOfLine[l.id] = l.po.id;
  });
  const first = combined.ids[0];
  const pick = (obj) => (poId) => Object.fromEntries(Object.entries(obj || {}).filter(([l]) => poOfLine[l] === poId));
  return Object.fromEntries(
    combined.ids.map((poId) => [
      poId,
      {
        counts: pick(progress.counts)(poId),
        boxes: pick(progress.boxes)(poId),
        scans: (progress.scans || []).filter((s) => (s.line_id ? poOfLine[s.line_id] === poId : poId === first)),
        startedAt: progress.startedAt || null,
      },
    ])
  );
}

const combine = (parts) => ({
  counts: Object.assign({}, ...parts.map((p) => p.counts || {})),
  boxes: Object.assign({}, ...parts.map((p) => p.boxes || {})),
  scans: parts.flatMap((p) => p.scans || []).sort((a, b) => String(a.at || '').localeCompare(String(b.at || ''))),
  startedAt: parts.map((p) => p.startedAt).filter(Boolean).sort()[0] || null,
});

/* One round trip for a receipt on screen. `snapshot` is the combined progress
   now; returns a function that turns the progress at answer time into the
   merged one (what the server has, plus whatever was counted meanwhile), or
   null when the server could not be reached. Bases are saved here. */
export async function syncReceipt(order, snapshot) {
  const epoch = clears;
  await flushClears();
  const parts = splitByOrder(order, snapshot);
  const items = [];
  order.ids.forEach((poId) => {
    // An answer that got lost does not mean the change did: it goes again under
    // the same id, which the server applies once. Only what came on top is new.
    let pending = safeGet(pendingKey(poId)) || [];
    const change = diff(parts[poId], pending.reduce((s, p) => apply(s, p.change), loadBase(poId)));
    if (!isEmpty(change)) {
      pending = [...pending, { id: newId(), change }];
      safeSet(pendingKey(poId), pending);
    }
    pending.forEach((p) => items.push({ po_id: poId, id: p.id, change: p.change }));
  });
  let server;
  try {
    const sent = items.length ? (await api.pushProgress(items)).progress : {};
    const rest = order.ids.filter((p) => !sent[String(p)]);
    const got = rest.length ? (await api.progress(rest)).progress : {};
    server = { ...got, ...sent };
  } catch {
    return null;
  }
  // validated meanwhile: this answer still has the counts that are now in Odoo
  if (epoch !== clears) return null;
  order.ids.forEach((poId) => safeDel(pendingKey(poId)));
  const states = order.ids.map((poId) => server[String(poId)]?.state || emptyProgress());
  order.ids.forEach((poId, i) => saveBase(poId, states[i]));
  const merged = combine(states);
  return (now) => {
    const next = settle(apply(merged, diff(now, snapshot)), order.lines);
    Object.entries(splitByOrder(order, next)).forEach(([poId, p]) => saveProgress(Number(poId), { ...emptyProgress(), ...p }));
    return next;
  };
}

async function flushClears() {
  const ids = safeGet(CLEARS_KEY) || [];
  if (!ids.length) return;
  try {
    await api.pushProgress(ids.map((po_id) => ({ po_id, clear: true })));
    safeDel(CLEARS_KEY);
  } catch {
    /* next time */
  }
}

/** The receipt is validated, or started over: zero on every device of this login. */
export function clearReceipt(poIds) {
  clears += 1;
  poIds.forEach((poId) => {
    saveProgress(poId, emptyProgress());
    saveBase(poId, emptyProgress());
    safeDel(pendingKey(poId));
  });
  safeSet(CLEARS_KEY, [...new Set([...(safeGet(CLEARS_KEY) || []), ...poIds])]);
  flushClears();
}

/** The server has settled this order's state (a receipt booked in part): this device takes it as is. */
export function adoptReceipt(poId, state) {
  clears += 1;
  const next = { ...emptyProgress(), ...state };
  saveProgress(poId, next);
  saveBase(poId, next);
  safeDel(pendingKey(poId));
}

/** Local progress of a receipt, for the first paint before the server answers. */
export const localProgress = (poIds) => combine(poIds.map(loadProgress));
